<template>
  <Head title="Notification Preferences" />

  <div class="py-6 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto space-y-6">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Notification Preferences
          </h1>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Customize how and when you receive notifications
          </p>
        </div>

        <!-- Quick Actions -->
        <div class="flex flex-wrap gap-2">
          <Button
            @click="enableAll"
            size="sm"
            variant="secondary"
            :disabled="isSaving"
          >
            <CheckCircleIcon class="h-4 w-4 mr-1" />
            Enable All
          </Button>

          <Button
            @click="disableAll"
            size="sm"
            variant="secondary"
            :disabled="isSaving"
          >
            <XCircleIcon class="h-4 w-4 mr-1" />
            Disable All
          </Button>

          <Button
            @click="confirmReset"
            size="sm"
            variant="danger"
            :disabled="isSaving"
          >
            <ArrowPathIcon class="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      <!-- No Company Warning -->
      <Alert
        v-if="!hasCompany"
        type="warning"
        title="No Company Selected"
        :dismissible="false"
      >
        Please select a company to manage notification preferences. Preferences are specific to each company.
      </Alert>

      <!-- Company Context -->
      <Card v-if="hasCompany && company">
        <div class="flex items-center space-x-3">
          <BuildingOfficeIcon class="h-8 w-8 text-gray-400 dark:text-gray-500" />
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Managing preferences for
            </p>
            <p class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
              {{ company.name }}
            </p>
          </div>
        </div>
      </Card>

      <!-- Main Preferences Form -->
      <form v-if="hasCompany" @submit.prevent="savePreferences" class="space-y-6">
        <!-- Email Notifications -->
        <Card>
          <div class="space-y-6">
            <!-- Section Header -->
            <div class="flex items-center space-x-3 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <EnvelopeIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Email Notifications
                </h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  Receive notifications via email
                </p>
              </div>
            </div>

            <!-- Email Preferences by Category -->
            <div class="space-y-6">
              <div
                v-for="category in categories"
                :key="category.value"
                class="space-y-3"
              >
                <!-- Category Header with Toggle -->
                <div class="flex items-start justify-between">
                  <div class="flex items-start space-x-3 flex-1">
                    <component
                      :is="category.icon"
                      class="h-6 w-6 text-gray-400 dark:text-gray-500 mt-0.5"
                    />
                    <div class="flex-1">
                      <label
                        :for="`email-${category.value}`"
                        class="text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
                      >
                        {{ category.label }}
                      </label>
                      <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {{ category.description }}
                      </p>
                    </div>
                  </div>

                  <ToggleSwitch
                    :id="`email-${category.value}`"
                    v-model="localPreferences[category.value].channel_email"
                    @update:model-value="handlePreferenceChange"
                  />
                </div>

                <!-- Frequency Selector (only if email is enabled) -->
                <div
                  v-if="localPreferences[category.value].channel_email"
                  class="ml-9 pl-3 border-l-2 border-gray-200 dark:border-gray-700"
                >
                  <label
                    :for="`email-freq-${category.value}`"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2"
                  >
                    Email Frequency
                  </label>
                  <Select
                    :id="`email-freq-${category.value}`"
                    v-model="localPreferences[category.value].frequency_email"
                    :options="emailFrequencyOptions"
                    @update:model-value="handlePreferenceChange"
                    class="max-w-xs"
                  />
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {{ getFrequencyDescription(localPreferences[category.value].frequency_email) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <!-- In-App Notifications -->
        <Card>
          <div class="space-y-6">
            <!-- Section Header -->
            <div class="flex items-center space-x-3 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div class="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <BellIcon class="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                  In-App Notifications
                </h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  Receive notifications in the notification center
                </p>
              </div>
            </div>

            <!-- In-App Preferences by Category -->
            <div class="space-y-4">
              <div
                v-for="category in categories"
                :key="category.value"
                class="flex items-start justify-between py-3"
              >
                <div class="flex items-start space-x-3 flex-1">
                  <component
                    :is="category.icon"
                    class="h-6 w-6 text-gray-400 dark:text-gray-500 mt-0.5"
                  />
                  <div class="flex-1">
                    <label
                      :for="`database-${category.value}`"
                      class="text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
                    >
                      {{ category.label }}
                    </label>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Show in notification center
                    </p>
                  </div>
                </div>

                <ToggleSwitch
                  :id="`database-${category.value}`"
                  v-model="localPreferences[category.value].channel_database"
                  @update:model-value="handlePreferenceChange"
                />
              </div>
            </div>

            <!-- Info Message -->
            <Alert type="info" :dismissible="false">
              In-app notifications are always shown in real-time. You can view them in the notification center.
            </Alert>
          </div>
        </Card>

        <!-- Save Button -->
        <div class="flex items-center justify-between">
          <p v-if="hasUnsavedChanges" class="text-sm text-orange-600 dark:text-orange-400">
            You have unsaved changes
          </p>

          <div class="flex gap-3 ml-auto">
            <Button
              v-if="hasUnsavedChanges"
              @click="discardChanges"
              type="button"
              variant="secondary"
              :disabled="isSaving"
            >
              Discard Changes
            </Button>

            <Button
              type="submit"
              :loading="isSaving"
              :disabled="isSaving || !hasUnsavedChanges"
            >
              <CheckIcon class="h-4 w-4 mr-1" />
              Save Preferences
            </Button>
          </div>
        </div>
      </form>

      <!-- Information Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Realtime Info -->
        <Card>
          <div class="space-y-3">
            <div class="flex items-center space-x-2">
              <ClockIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 class="font-semibold text-gray-900 dark:text-white">
                Realtime Notifications
              </h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Realtime notifications are sent immediately when events occur.
              You can choose which categories to receive via email.
            </p>
          </div>
        </Card>

        <!-- Security Note -->
        <Card>
          <div class="space-y-3">
            <div class="flex items-center space-x-2">
              <ShieldCheckIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 class="font-semibold text-gray-900 dark:text-white">
                Security Alerts
              </h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              System security alerts are always sent via email and cannot be disabled.
              This ensures you're notified of any security-related events.
            </p>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import {
  BellIcon,
  EnvelopeIcon,
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  AtSymbolIcon,
  CogIcon,
  ShieldCheckIcon,
  ClockIcon,
} from '@heroicons/vue/24/outline';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import ToggleSwitch from '@/Components/Common/ToggleSwitch.vue';
import Alert from '@/Components/Common/Alert.vue';
import { useToast } from '@/Composables/useToast';
import axios from 'axios';

// Props from controller
const props = defineProps({
  preferences: {
    type: Object,
    required: true,
  },
  categories: {
    type: Array,
    required: true,
  },
  hasCompany: {
    type: Boolean,
    default: false,
  },
  company: {
    type: Object,
    default: null,
  },
});

// Composables
const { success, error: showError } = useToast();

// State
const localPreferences = ref({});
const originalPreferences = ref({});
const isSaving = ref(false);
const hasUnsavedChanges = ref(false);

// Initialize preferences
onMounted(() => {
  initializePreferences();
});

const initializePreferences = () => {
  // Deep clone preferences
  localPreferences.value = JSON.parse(JSON.stringify(props.preferences));
  originalPreferences.value = JSON.parse(JSON.stringify(props.preferences));
};

// Enhanced categories with icons
const categories = computed(() => {
  const iconMap = {
    tickets: TicketIcon,
    work_orders: WrenchScrewdriverIcon,
    incidents: ExclamationTriangleIcon,
    team: UserGroupIcon,
    mentions: AtSymbolIcon,
    company: BuildingOfficeIcon,
    system: CogIcon,
  };

  return props.categories.map(cat => ({
    ...cat,
    icon: iconMap[cat.value] || BellIcon,
  }));
});

// Email frequency options
const emailFrequencyOptions = [
  {
    value: 'realtime',
    label: 'Realtime',
  },
  {
    value: 'disabled',
    label: 'Disabled',
  },
];

// Methods
const handlePreferenceChange = () => {
  hasUnsavedChanges.value = JSON.stringify(localPreferences.value) !== JSON.stringify(originalPreferences.value);
};

const savePreferences = async () => {
  try {
    isSaving.value = true;

    await axios.put(route('profile.notifications.update'), {
      preferences: localPreferences.value,
    });

    // Update original preferences
    originalPreferences.value = JSON.parse(JSON.stringify(localPreferences.value));
    hasUnsavedChanges.value = false;

    success('Notification preferences saved successfully');
  } catch (error) {
    console.error('Failed to save preferences:', error);
    showError('Failed to save preferences. Please try again.');
  } finally {
    isSaving.value = false;
  }
};

const enableAll = () => {
  categories.value.forEach(category => {
    localPreferences.value[category.value].channel_email = true;
    localPreferences.value[category.value].channel_database = true;
    localPreferences.value[category.value].frequency_email = 'realtime';
    localPreferences.value[category.value].frequency_database = 'realtime';
  });

  handlePreferenceChange();
  success('All notifications enabled');
};

const disableAll = () => {
  categories.value.forEach(category => {
    localPreferences.value[category.value].channel_email = false;
    localPreferences.value[category.value].channel_database = false;
    localPreferences.value[category.value].frequency_email = 'disabled';
    localPreferences.value[category.value].frequency_database = 'disabled';
  });

  handlePreferenceChange();
  success('All notifications disabled');
};

const confirmReset = async () => {
  if (!confirm('Are you sure you want to reset all notification preferences to defaults? This will enable all notifications.')) {
    return;
  }

  try {
    isSaving.value = true;

    await axios.post(route('profile.notifications.reset'));

    router.reload({
      preserveScroll: true,
      onSuccess: () => {
        success('Preferences reset to defaults');
        hasUnsavedChanges.value = false;
      },
    });
  } catch (error) {
    console.error('Failed to reset preferences:', error);
    showError('Failed to reset preferences. Please try again.');
  } finally {
    isSaving.value = false;
  }
};

const discardChanges = () => {
  if (!confirm('Are you sure you want to discard your changes?')) {
    return;
  }

  localPreferences.value = JSON.parse(JSON.stringify(originalPreferences.value));
  hasUnsavedChanges.value = false;
  success('Changes discarded');
};

const getFrequencyDescription = (frequency) => {
  const descriptions = {
    realtime: 'Receive emails immediately when events occur',
    disabled: 'No email notifications for this category',
  };

  return descriptions[frequency] || '';
};
</script>
